import React from 'react';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_HIGHEST,
} from '../brandDashboardTokens';

export interface CampaignReportSyncBannerProps {
    syncPct: number;
    syncing: boolean;
    onRefresh: () => void;
    onQueueSync: () => void;
}

export function CampaignReportSyncBanner({ syncPct, syncing, onRefresh, onQueueSync }: CampaignReportSyncBannerProps) {
    if (syncPct >= 100) return null;

    return (
        <Box
            sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                border: `1px solid ${BRAND_OUTLINE_VARIANT}44`,
                position: 'relative',
                overflow: 'hidden',
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    top: -80,
                    right: -80,
                    width: 160,
                    height: 160,
                    bgcolor: `${BRAND_LUXE_PRIMARY}10`,
                    filter: 'blur(60px)',
                    borderRadius: '50%',
                }}
            />
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    alignItems: { sm: 'center' },
                    justifyContent: 'space-between',
                    mb: 2,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <SyncIcon
                        sx={{
                            color: BRAND_LUXE_PRIMARY,
                            animation: 'spin 3s linear infinite',
                            '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
                        }}
                    />
                    <Box>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff' }}>
                            Syncing metrics…
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
                            Metrics update automatically every few hours.
                        </Typography>
                    </Box>
                </Box>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 28, color: BRAND_LUXE_PRIMARY }}>
                    {Math.round(syncPct)}%
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={Math.min(100, syncPct)}
                sx={{
                    height: 8,
                    borderRadius: 999,
                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                    '& .MuiLinearProgress-bar': {
                        borderRadius: 999,
                        background: `linear-gradient(90deg, ${BRAND_LUXE_PRIMARY}, #feb700)`,
                        boxShadow: `0 0 8px ${BRAND_LUXE_PRIMARY}66`,
                    },
                }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                <Button
                    size="small"
                    onClick={onQueueSync}
                    disabled={syncing}
                    sx={{ textTransform: 'none', color: BRAND_LUXE_PRIMARY }}>
                    Queue sync
                </Button>
                <Button
                    size="small"
                    onClick={onRefresh}
                    sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.65)' }}>
                    Refresh dashboard
                </Button>
            </Box>
        </Box>
    );
}

export function CampaignReportEmptyState({ onRefresh }: { onRefresh: () => void }) {
    return (
        <Box
            sx={{
                minHeight: 420,
                borderRadius: 3,
                bgcolor: BRAND_SURFACE_CONTAINER,
                border: `1px solid ${BRAND_OUTLINE_VARIANT}33`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                px: 3,
                py: 6,
            }}>
            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: { xs: 26, md: 34 }, color: '#fff', mb: 1.5 }}>
                No published posts yet
            </Typography>
            <Typography sx={{ fontSize: 15, color: 'rgba(255,255,255,0.55)', maxWidth: 420, lineHeight: 1.6, mb: 3 }}>
                Creators are producing content. Check back once the first live URLs are submitted — we will pull metrics
                via their connected accounts.
            </Typography>
            <Button
                onClick={onRefresh}
                sx={{
                    px: 3,
                    py: 1.25,
                    borderRadius: 999,
                    textTransform: 'none',
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    color: '#392700',
                    bgcolor: BRAND_LUXE_PRIMARY,
                }}>
                Refresh dashboard
            </Button>
        </Box>
    );
}
